import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Image } from "react-bootstrap";
import { FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import "../App.css";
import logo from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "http://localhost:3023/api";

const API_ENDPOINTS = {
  getTemplateDetails: (templateId) =>
    `${API_BASE}/getTemplateDetails/${templateId}`,
};

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

const getAxiosConfig = () => {
  const token = getAuthToken();
  if (!token) return {};
  return { headers: { Authorization: `Bearer ${token}` } };
};

const normalizeInputType = (type = "") => {
  const value = String(type).toLowerCase();
  if (value === "multiple") return "multiple_choice";
  return value;
};

const PreviewAssessmentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const templateId = location.state?.template_id || null;
  const isAdminPreview = Boolean(templateId);

  const [toast, setToast] = useState(false);
  const [template, setTemplate] = useState(null);
  const [elements, setElements] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [meta, setMeta] = useState({
    studentName: "",
    trainer: "",
    startDate: getTodayDate(),
    endDate: getTodayDate(),
    attendance: "",
  });

  const loadTemplateDetails = useCallback(async () => {
    if (!templateId) return;

    setLoading(true);
    setMessage("");

    try {
      const response = await axios.get(
        API_ENDPOINTS.getTemplateDetails(templateId),
        getAxiosConfig()
      );

      setTemplate(response.data?.template || null);
      setElements(
        Array.isArray(response.data?.questions) ? response.data.questions : []
      );
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to load assessment template."
      );
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplateDetails();
  }, [loadTemplateDetails]);

  const previewTitle = useMemo(() => {
    return template?.title || "Academic Staff Appraisal Form";
  }, [template]);

  const previewDescription = useMemo(() => {
    return (
      template?.description ||
      "Please fill in the details below for the general appraisal."
    );
  }, [template]);

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  const handleRadioChange = (elementId, value) => {
    setAnswers((prev) => ({ ...prev, [elementId]: value }));
  };

  const handleCheckboxChange = (elementId, value, checked) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[elementId]) ? prev[elementId] : [];

      return {
        ...prev,
        [elementId]: checked
          ? [...current, value]
          : current.filter((item) => item !== value),
      };
    });
  };

  const handleTextChange = (elementId, value) => {
    setAnswers((prev) => ({ ...prev, [elementId]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setToast(true);
    setTimeout(() => setToast(false), 1800);
  };

  const handleClear = () => {
    setMeta({
      studentName: "",
      trainer: "",
      startDate: getTodayDate(),
      endDate: getTodayDate(),
      attendance: "",
    });
    setAnswers({});
  };

  const handleClose = () => {
    navigate(-1);
  };

  const renderElementInput = (item) => {
    const type = normalizeInputType(item.type);
    const elementId = item.element_id || item.id;
    const answersList = Array.isArray(item.answers) ? item.answers : [];

    if (type === "short_answer") {
      return (
        <input
          className="form-preview-input"
          value={answers[elementId] || ""}
          onChange={(e) => handleTextChange(elementId, e.target.value)}
          placeholder="Type a placeholder"
          required={Number(item.is_required) === 1}
        />
      );
    }

    if (type === "long_answer") {
      return (
        <textarea
          className="form-preview-textarea"
          value={answers[elementId] || ""}
          onChange={(e) => handleTextChange(elementId, e.target.value)}
          placeholder="Type a placeholder"
          required={Number(item.is_required) === 1}
        />
      );
    }

    return (
      <div className="form-preview-option-list">
        {answersList.map((answer, answerIndex) => {
          const answerValue = answer.answer || "";

          if (type === "multiple_choice") {
            const checked = Array.isArray(answers[elementId])
              ? answers[elementId].includes(answerValue)
              : false;

            return (
              <label
                className="form-preview-option"
                key={answer.answer_id || answerIndex}
              >
                <input
                  type="checkbox"
                  value={answerValue}
                  checked={checked}
                  onChange={(e) =>
                    handleCheckboxChange(
                      elementId,
                      answerValue,
                      e.target.checked
                    )
                  }
                />
                <span>{answerValue}</span>
              </label>
            );
          }

          return (
            <label
              className="form-preview-option"
              key={answer.answer_id || answerIndex}
            >
              <input
                type="radio"
                name={`element_${elementId}`}
                value={answerValue}
                checked={answers[elementId] === answerValue}
                onChange={(e) => handleRadioChange(elementId, e.target.value)}
                required={Number(item.is_required) === 1 && answerIndex === 0}
              />
              <span>{answerValue}</span>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div className="form-preview-page">
      <form className="form-preview-paper wide" onSubmit={handleSubmit}>
        <div className="form-preview-banner">
          <div className="form-preview-logo-box">
            <Image src={logo} className="brand-logo" alt="TMIS Logo" />
          </div>

          <div className="form-preview-diagonal one"></div>
          <div className="form-preview-diagonal two"></div>
          <div className="form-preview-diagonal three"></div>

          <button
            type="button"
            className="form-preview-close-btn"
            onClick={handleClose}
          >
            <FiX />
          </button>
        </div>

        <div className="form-preview-body wide">
          <div className="form-preview-title">
            <h1>{previewTitle}</h1>
            <p>{previewDescription}</p>
          </div>

          {message && <div className="assessment-error">{message}</div>}

          <section className="form-preview-section">
            <h2>Personal Information</h2>

            <div className="form-preview-field">
              <label>
                Name <span>*</span>
              </label>

              <input
                value={meta.studentName}
                onChange={(e) =>
                  handleMetaChange("studentName", e.target.value)
                }
                placeholder="Type a placeholder"
              />

              <div className="form-preview-hints single">
                <em>Full Name</em>
              </div>
            </div>

            <div className="form-preview-field">
              <label>
                Name of Trainer <span>*</span>
              </label>
              <input
                value={meta.trainer}
                onChange={(e) => handleMetaChange("trainer", e.target.value)}
                placeholder="Type a placeholder"
              />
            </div>

            <div className="form-preview-two-col">
              <div className="form-preview-field">
                <label>
                  Start Date <span>*</span>
                </label>
                <input
                  type="date"
                  value={meta.startDate}
                  onChange={(e) =>
                    handleMetaChange("startDate", e.target.value)
                  }
                />
              </div>

              <div className="form-preview-field">
                <label>
                  End Date <span>*</span>
                </label>
                <input
                  type="date"
                  value={meta.endDate}
                  onChange={(e) => handleMetaChange("endDate", e.target.value)}
                />
              </div>
            </div>

            <div className="form-preview-field">
              <label>Student Attendance</label>
              <input
                value={meta.attendance}
                onChange={(e) => handleMetaChange("attendance", e.target.value)}
                placeholder="Type a placeholder"
              />
            </div>
          </section>

          <section className="form-preview-section">
            <h2>Performance Evaluation</h2>

            {loading ? (
              <div className="assessment-loading">
                Loading assessment template...
              </div>
            ) : elements.length === 0 ? (
              <div className="form-preview-empty">No elements found</div>
            ) : (
              elements.map((item, index) => (
                <div
                  className="form-preview-field"
                  key={item.element_id || index}
                >
                  <label>
                    {item.element || "Untitled element"}
                    {Number(item.is_required) === 1 && <span> *</span>}
                  </label>
                  {renderElementInput(item)}
                </div>
              ))
            )}
          </section>

          <section className="form-preview-section">
            <h2>Sign-off</h2>

            <div className="form-preview-two-col sign">
              <div className="form-preview-field">
                <label>Trainer Name & Signature</label>
                <input placeholder="Type a placeholder" />
              </div>

              <div className="form-preview-field">
                <label>Date</label>
                <input type="date" defaultValue={getTodayDate()} />
              </div>

              <div className="form-preview-field">
                <label>Checked by</label>
                <input placeholder="Type a placeholder" />
              </div>

              <div className="form-preview-field">
                <label>Endorsed by</label>
                <input placeholder="Type a placeholder" />
              </div>
            </div>
          </section>

          <div className="form-preview-footer">
            <button type="button" onClick={handleClear}>
              Clear
            </button>

            <button type="submit">
              {isAdminPreview ? "Preview Only" : "Submit Assessment"}
            </button>
          </div>
        </div>
      </form>

      {toast && (
        <div className="assessment-toast">
          {isAdminPreview ? "Preview only" : "Assessment submitted"}
        </div>
      )}
    </div>
  );
};

export default PreviewAssessmentPage;